// Auth API — xsib-core `identity` moduli bilan haqiqiy kontrakt.
//
// NEGA ZOD, oddiy TS interfeysi emas: TS tipi kompilyatsiyadan keyin yo'qoladi
// va serverdan kelgan `undefined` maydon jimgina o'tib ketadi — xato esa uch
// qadam narida, boshqa komponentda chiqadi. Zod xatoni CHEGARADA ushlaydi.
//
// ⚠️ TARTIB: parol → TOTP → tenant tanlash. Ilgari tenant TOTP'dan OLDIN
// so'ralardi (sir tenantga bog'langan edi). Endi bitta odam — bitta hisob,
// bitta parol, bitta TOTP (tashkilotdan mustaqil), shuning uchun tashkilot
// ENG OXIRIDA tanlanadi — faqat kim ekani (parol+TOTP) aniqlangandan keyin.
//
// Uch bosqichning har biri O'Z shaklida javob beradi:
//
//   POST /api/auth/login {username, password}
//     → 401 {code:"BAD_CREDENTIALS", ...}          — errors.ts orqali, bu yerda YO'Q
//     → 200 {status:"TOTP_SETUP", setupToken, qrUri, secret}   — birinchi marta
//     → 200 {status:"TOTP_REQUIRED", totpToken}                — har safar
//   Login HECH QACHON to'g'ridan-to'g'ri token qaytarmaydi — TOTP endi
//   MAJBURIY, tenantdan mustaqil.
//
//   POST /api/auth/totp/confirm {setupToken, code}
//   POST /api/auth/totp/verify  {totpToken, code}
//     → 200 {status:"CHOOSE_TENANT", choiceToken, candidates:[...]}  — ko'p a'zolik
//     → 200 {token, expiresAt, user}                                — bitta a'zolik, tugadi
//     → 403                                                         — faol a'zolik yo'q
//
//   POST /api/auth/login/tenant {choiceToken, tenantId} → {token, expiresAt, user}
//   Bu HAR DOIM yakuniy shaklga olib keladi — ichida yana branch yo'q.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { getTenantPrefs } from '@/shared/lib/tenantPrefs'
import { SCOPE_TYPES, isPermission, type Permission, type ScopeType } from './permissions'
import { getRefreshToken } from './tokenStorage'

const scopeSchema = z.object({
  type: z.enum(SCOPE_TYPES),
  /** SUBTREE / VENUE uchun — qaysi tugun yoki ob'ekt. GLOBAL/TENANT da umuman kelmaydi */
  id: z.string().nullish(),
})

/**
 * `GET /api/me` va yakuniy kirish javobidagi `user` — AYNAN bir xil
 * yozuv (`AuthController.MeResponse`), shuning uchun bitta sxema.
 *
 * ⚠️ SHAKL TEKIS: `{userId, tenantId, username, permissions, scopes}`.
 * Tenant haqida `tenantId` dan boshqa hech narsa YO'Q — valyuta ham,
 * nom ham, vaqt zonasi ham. `tenant` maydoni backend uni qo'shgan kunda
 * ISHLASHI UCHUN oldindan ixtiyoriy qilib qo'yilgan: u kelsa darhol
 * ustun bo'ladi va `tenantPrefs` dagi qo'lda tanlov keraksiz qoladi.
 */
const meSchema = z.object({
  userId: z.string(),
  tenantId: z.string(),
  username: z.string(),
  displayName: z.string().nullish(),
  /** Rollarning birlashgan huquqlari (04-ROLES.md §7) */
  permissions: z.array(z.string()),
  scopes: z.array(scopeSchema).default([]),
  /** Backend hali BERMAYDI — yuqoridagi izohga qarang */
  tenant: z
    .object({
      name: z.string().nullish(),
      currency: z.string().nullish(),
      dayTimezone: z.string().nullish(),
      businessKind: z.enum(['ISP', 'VENUE']).nullish(),
    })
    .nullish(),
})

export type MeResponseRaw = z.infer<typeof meSchema>

export interface Scope {
  type: ScopeType
  id: string | null
}

export interface CurrentUser {
  id: string
  username: string
  displayName: string
  tenant: {
    id: string
    name: string
    /** ISO 4217. Manbai: `/api/me` → yo'q bo'lsa brauzerdagi tanlov → `null` */
    currency: string | null
    timezone: string | null
    businessKind: 'ISP' | 'VENUE' | null
  }
  permissions: ReadonlySet<Permission>
  scopes: readonly Scope[]
}

function toCurrentUser(raw: MeResponseRaw): CurrentUser {
  // Bizga noma'lum huquq nomlari e'tiborsiz qoldiriladi: backend biz
  // bilmaydigan yangisini qo'shgan bo'lishi mumkin, panel esa yiqilmasligi
  // kerak. (Jonli tizimda aynan shunday: backend `user.update` yuboradi,
  // frontend katalogida u yo'q.)
  const permissions = new Set<Permission>()
  for (const value of raw.permissions) {
    if (isPermission(value)) permissions.add(value)
  }

  // Serverniki USTUN, brauzerdagi tanlov — zaxira
  const prefs = getTenantPrefs(raw.tenantId)

  return {
    id: raw.userId,
    username: raw.username,
    displayName: raw.displayName ?? raw.username,
    tenant: {
      id: raw.tenantId,
      name: raw.tenant?.name ?? raw.tenantId,
      currency: raw.tenant?.currency ?? prefs.currency ?? null,
      timezone: raw.tenant?.dayTimezone ?? prefs.timezone ?? null,
      businessKind: raw.tenant?.businessKind ?? null,
    },
    permissions,
    scopes: raw.scopes.map((s) => ({ type: s.type, id: s.id ?? null })),
  }
}

/** Yakuniy kirish javobi — token VA foydalanuvchi bir paketda. */
const loginResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string().nullish(),
  /**
   * 14 kunlik yangilash tokeni (`AuthResource.present`).
   *
   * ⚠️ `nullish` ATAYLAB: majburiy qilinsa, uni bermaydigan backend
   * versiyasida `parse` yiqiladi va odam UMUMAN kira olmaydi. Kelmasa
   * sessiya avvalgidek 30 daqiqada tugaydi — bu "kira olmaslik" dan
   * ancha yengil.
   *
   * ⚠️ Bu qiymat AYLANADI — batafsil `sessionRefresh.ts` da.
   */
  refreshToken: z.string().nullish(),
  user: meSchema,
})

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginSuccess {
  token: string
  expiresAt: string | null
  /** `null` — backend bermadi; u holda sessiya yangilanmaydi (yuqoridagi izoh) */
  refreshToken: string | null
  user: CurrentUser
}

function toLoginSuccess(parsed: z.infer<typeof loginResponseSchema>): LoginSuccess {
  return {
    token: parsed.token,
    expiresAt: parsed.expiresAt ?? null,
    refreshToken: parsed.refreshToken ?? null,
    user: toCurrentUser(parsed.user),
  }
}

/** `CHOOSE_TENANT` bosqichidagi bitta tashkilot kartasi uchun ma'lumot */
const tenantCandidateSchema = z.object({
  tenantId: z.string(),
  code: z.string(),
  name: z.string(),
  currency: z.string().nullish(),
  businessKind: z.enum(['ISP', 'VENUE']).nullish(),
})

export interface TenantCandidate {
  tenantId: string
  code: string
  name: string
  currency: string | null
  businessKind: 'ISP' | 'VENUE' | null
}

const chooseTenantSchema = z.object({
  status: z.literal('CHOOSE_TENANT'),
  choiceToken: z.string(),
  candidates: z.array(tenantCandidateSchema),
})

function toTenantCandidates(parsed: z.infer<typeof chooseTenantSchema>): TenantCandidate[] {
  return parsed.candidates.map((c) => ({
    tenantId: c.tenantId,
    code: c.code,
    name: c.name,
    currency: c.currency ?? null,
    businessKind: c.businessKind ?? null,
  }))
}

const totpSetupSchema = z.object({
  status: z.literal('TOTP_SETUP'),
  setupToken: z.string(),
  qrUri: z.string(),
  secret: z.string(),
})

const totpRequiredSchema = z.object({
  status: z.literal('TOTP_REQUIRED'),
  totpToken: z.string(),
})

/**
 * Kirishning navbatdagi qadami. Login HAR DOIM shu ikkitadan biriga olib
 * keladi — TOTP endi tenantdan mustaqil va MAJBURIY, ya'ni login o'zi
 * hech qachon yakuniy tokenni ham, tashkilot tanlovini ham qaytarmaydi.
 */
export type LoginOutcome =
  | { kind: 'totpSetup'; setupToken: string; qrUri: string; secret: string }
  | { kind: 'totpRequired'; totpToken: string }

/**
 * `status` maydoniga qarab javob shaklini aniqlaydi (oraliq bosqichlarda
 * bor, yakuniy javobda YO'Q).
 */
const statusPeekSchema = z
  .object({
    status: z.enum(['CHOOSE_TENANT', 'TOTP_SETUP', 'TOTP_REQUIRED']).optional(),
  })
  .passthrough()

export async function login(request: LoginRequest): Promise<LoginOutcome> {
  const response = await api.post<unknown>('/api/auth/login', request)
  const peek = statusPeekSchema.parse(response.data)

  if (peek.status === 'TOTP_SETUP') {
    const parsed = totpSetupSchema.parse(response.data)
    return {
      kind: 'totpSetup',
      setupToken: parsed.setupToken,
      qrUri: parsed.qrUri,
      secret: parsed.secret,
    }
  }

  if (peek.status === 'TOTP_REQUIRED') {
    const parsed = totpRequiredSchema.parse(response.data)
    return { kind: 'totpRequired', totpToken: parsed.totpToken }
  }

  // Login endi boshqa shaklda javob BERMASLIGI kerak (TOTP tenantdan
  // mustaqil bo'lgach, u har doim ikkalasidan biri). Kelib qolsa — bu
  // kontraktdagi kutilmagan o'zgarish, zod xatosi orqali CHEGARADA ushlanadi.
  throw new Error(`login: kutilmagan javob shakli — ${JSON.stringify(response.data)}`)
}

/**
 * TOTP tasdiqlash/tekshiruv natijasi. Endi UCHTA holat bor:
 *   · `authenticated` — bitta a'zolik, kirish shu yerda tugaydi
 *   · `chooseTenant`  — bir nechta a'zolik, tashkilot tanlash kerak
 *   · `noMembership`  — hisobning FAOL a'zoligi yo'q (backend 403 qaytaradi)
 */
export type TotpOutcome =
  | ({ kind: 'authenticated' } & LoginSuccess)
  | { kind: 'chooseTenant'; choiceToken: string; candidates: TenantCandidate[] }
  | { kind: 'noMembership' }

/** `totp/confirm` va `totp/verify` bir xil javob shaklini beradi — umumiy ishlov */
async function postTotp(url: string, body: unknown): Promise<TotpOutcome> {
  let data: unknown
  try {
    const response = await api.post<unknown>(url, body)
    data = response.data
  } catch (error) {
    // 403 — hisobning HECH QANDAY faol a'zoligi yo'q. Bu boshqa 403'lardan
    // (huquq yetmasligi) farqli holat: bu yerda hali sessiya UMUMAN yo'q,
    // shuning uchun oddiy xato emas, aniq ekranga yo'naltiriladigan natija
    // sifatida qaytariladi (`TotpPage` buni ushlab maxsus sahifaga o'tadi).
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return { kind: 'noMembership' }
    }
    throw error
  }

  const peek = statusPeekSchema.parse(data)
  if (peek.status === 'CHOOSE_TENANT') {
    return { kind: 'chooseTenant', ...toTenantCandidatesResult(chooseTenantSchema.parse(data)) }
  }

  return { kind: 'authenticated', ...toLoginSuccess(loginResponseSchema.parse(data)) }
}

function toTenantCandidatesResult(parsed: z.infer<typeof chooseTenantSchema>) {
  return { choiceToken: parsed.choiceToken, candidates: toTenantCandidates(parsed) }
}

// ---------------------------------------------------------------------------
// Sessiya DAVOMIDA tashkilotni almashtirish
//
// ⚠️ Bu kirish oqimining bir qismi EMAS. Farqi shakl darajasida ham
// ko'rinadi: `chooseTenant` `choiceToken` bilan ishlaydi va tokensiz
// chaqiriladi; bu yerdagi ikkalasi esa MAVJUD sessiya tokeni bilan
// chaqiriladi (`client.ts` interceptor'i uni o'zi qo'shadi).
// ---------------------------------------------------------------------------

/** `GET /api/auth/tenants` — backend `TenantSummary` yozuvi. */
const myTenantSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  currency: z.string().nullish(),
  dayTimezone: z.string().nullish(),
  locale: z.string().nullish(),
  businessKind: z.enum(['ISP', 'VENUE']).nullish(),
})

export interface MyTenant {
  id: string
  code: string
  name: string
  currency: string | null
  businessKind: 'ISP' | 'VENUE' | null
}

/**
 * Foydalanuvchi o'ta oladigan tashkilotlar.
 *
 * ⚠️ Ro'yxat HAR DOIM serverdan olinadi va `me` javobidan YASALMAYDI:
 * `me` faqat JORIY tashkilotni biladi. Uni tokendan yig'ishga urinish
 * ham noto'g'ri bo'lardi — token 30 daqiqa yashaydi, a'zolik esa o'sha
 * vaqt ichida bekor qilinishi mumkin.
 */
export async function fetchMyTenants(): Promise<MyTenant[]> {
  const response = await api.get<unknown>('/api/auth/tenants')
  return z
    .array(myTenantSchema)
    .parse(response.data)
    .map((t) => ({
      id: t.id,
      code: t.code,
      name: t.name,
      currency: t.currency ?? null,
      businessKind: t.businessKind ?? null,
    }))
}

/**
 * Tashkilotni almashtirish — qayta kirmasdan.
 *
 * ⚠️ Javob AYNAN kirish javobi shaklida (`{token, expiresAt, user}`), va
 * bu ataylab: panelda sessiyani saqlashning YAGONA yo'li bo'lib qoladi
 * (`AuthProvider.finishLogin`). Ikkinchi shox yozilsa, biri (masalan
 * valyutani o'rnatish) ertaga unutilardi.
 *
 * ⚠️ Server eski access tokenni SHU chaqiruvda bekor qiladi. Ya'ni
 * chaqiruvchi javobni olgach saqlangan tokenni ALBATTA almashtirishi
 * kerak — aks holda keyingi so'rov 401 bo'ladi.
 */
export async function switchTenant(tenantId: string): Promise<LoginSuccess> {
  const response = await api.post<unknown>('/api/auth/switch-tenant', { tenantId })
  return toLoginSuccess(loginResponseSchema.parse(response.data))
}

/** `CHOOSE_TENANT` kartasida tashkilot tanlanganda — bu ENDI YAKUNIY qadam */
export async function chooseTenant(choiceToken: string, tenantId: string): Promise<LoginSuccess> {
  const response = await api.post<unknown>('/api/auth/login/tenant', { choiceToken, tenantId })
  return toLoginSuccess(loginResponseSchema.parse(response.data))
}

/** Birinchi TOTP sozlashda — QR skanerlangandan/sirni kiritgandan keyin */
export async function confirmTotp(setupToken: string, code: string): Promise<TotpOutcome> {
  return postTotp('/api/auth/totp/confirm', { setupToken, code })
}

/** Har safargi kirishda — TOTP kod tekshiruvi */
export async function verifyTotp(totpToken: string, code: string): Promise<TotpOutcome> {
  return postTotp('/api/auth/totp/verify', { totpToken, code })
}

/**
 * TOTP'ni qayta o'rnatish — sozlamalardagi xavfli amal. Foydalanuvchi
 * keyingi kirishda QR'ni qaytadan skanerlashi kerak bo'ladi
 * (`TOTP_SETUP` bosqichi qayta ishga tushadi).
 */
export async function resetTotp(userId: string): Promise<void> {
  await api.post(`/api/users/${userId}/totp/reset`)
}

export async function fetchMe(): Promise<CurrentUser> {
  const response = await api.get<unknown>('/api/me')
  return toCurrentUser(meSchema.parse(response.data))
}

/**
 * Chiqish — tokenlarni SERVER tomonda ham bekor qiladi.
 *
 * ⚠️ Bu funksiya ilgari bo'sh `Promise` qaytarardi va izohida
 * "`POST /api/auth/logout` YO'Q, JWT holatsiz" deb yozilgan edi. Bu
 * ma'lumot ESKIRGAN: `AuthResource.logout` mavjud, u refresh tokenini
 * bekor qiladi VA joriy access tokenni qora ro'yxatga qo'shadi.
 *
 * ⚠️ Refresh oqimi ulangandan keyin bu SHART bo'lib qoldi: ilgari
 * mahalliy tozalash amalda yetarli edi (token 30 daqiqada o'zi
 * tugardi), endi esa saqlanmagan holda ham refresh tokeni 14 KUN
 * yashaydi. Umumiy kompyuterda "Chiqish" bosgan odam uchun bu ikki
 * haftalik teshik bo'lardi.
 *
 * ⚠️ Xato YUTILADI: tarmoq uzilgan yoki token allaqachon yaroqsiz
 * bo'lsa ham odam CHIQISHI kerak. Chaqiruvchi (`AuthProvider.signOut`)
 * shundan keyin mahalliy sessiyani baribir tozalaydi.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken()
  // Backend `@NotBlank` talab qiladi — qiymatsiz so'rov 400 bo'lardi.
  // Bekor qiladigan narsa yo'q, ya'ni chaqiruvning ma'nosi ham yo'q.
  if (refreshToken === null) return
  try {
    await api.post('/api/auth/logout', { refreshToken })
  } catch {
    // ataylab jim — yuqoridagi izoh
  }
}
